/**
 * Form class that contains multiple elements.
 * Provides methods to manage elements and validate the form.
 */
import { FormsElementTypeEnum, FormsTypeEnum } from 'generated/resources/graphql'

import { Element } from 'modules/forms/lib/Element'
import { SectionElement } from 'modules/forms/lib/elements/SectionElement'
import { Answer } from 'modules/forms/lib/types'

const AllowedParamTypes = new Set(['string', 'number', 'boolean'])
function isSectionElement(el: Element | SectionElement): el is SectionElement {
  return 'elements' in el && Array.isArray(el.elements)
}

function isValidParam<T>(value: unknown, defaultValue?: T): value is T {
  //if we have a default value, param is valid if the same type
  if (defaultValue) {
    return typeof value === typeof defaultValue
  }

  //we don't have default value, param is valid if is string, number or boolean
  return AllowedParamTypes.has(typeof value)
}

type AnswersBackend = {
  elementId: string
} & Answer

export class Form {
  /**
   * Unique identifier for the form
   */
  id: string

  /**
   * The title of the form
   */
  title: string

  /**
   * Description of the form
   */
  description: string | null

  /**
   * Collection of elements in the form
   */
  elements: Element[]

  /**
   * Custom parameters specific to the form
   */
  params: Record<string, unknown>

  /**
   * Type of the form resource
   */
  resourceType: string

  /**
   * Id of the form resource
   */
  resourceId: string | null

  /**
   * The form status
   */
  status: string

  /**
   * The form type
   */
  formType: FormsTypeEnum

  /**
   * Whether responses are anonymous
   */
  anonymous: boolean

  /**
   * The form updated date
   */
  updatedAt: string | undefined

  /**
   * The form creation date
   */
  createdAt: string | undefined

  /**
   * The form author employee
   */
  authorFullName: string | undefined

  /**
   * The form author avatar URL
   */
  authorAvatarUrl: string | undefined

  /**
   * The form configuration
   */
  configuration?: {
    allowedElements?: FormsElementTypeEnum[]
    retryable?: boolean
    scorable?: boolean
  } | null

  /**
   * Constructor for the Form class
   *
   * @param id - Unique identifier for the form
   * @param resourceId - Id of the form resource
   * @param resourceType - Type of the form resource
   * @param title - The title of the form
   * @param description - Description of the form
   * @param elements - Initial collection of elements
   * @param params - Custom parameters specific to the form
   */
  constructor(
    id: string,
    title: string,
    description: string | null,
    resourceType: string,
    resourceId: string | null,
    formType: FormsTypeEnum,
    status: string,
    anonymous: boolean,
    elements: Element[] = [],
    params: Record<string, unknown> = {},
    configuration: {
      allowedElements?: FormsElementTypeEnum[]
      retryable?: boolean
      scorable?: boolean
    } | null = null
  ) {
    this.id = id
    this.title = title
    this.description = description
    this.elements = elements
    this.params = params
    this.resourceType = resourceType
    this.resourceId = resourceId
    this.status = status
    this.formType = formType
    this.anonymous = anonymous
    this.configuration = configuration
    // Sort elements by position
    this.sortElements()
  }

  /**
   * Create a new form
   *
   * @param title - The title of the form
   * @param resourceId - Id of the form resource
   * @param resourceType - Type of the form resource
   * @param anonymous - Whether responses are anonymous
   * @param description - The description of the form
   * @param formType - The form type
   * @param status - The form status
   * @returns A new form instance
   */
  static create({
    title,
    resourceId,
    resourceType,
    anonymous = false,
    description = '',
    formType = 'custom',
    status = 'draft',
  }: {
    title: string
    resourceId: string
    resourceType: string
    anonymous?: boolean
    description?: string
    formType?: FormsTypeEnum
    status?: string
  }): Form {
    const f = new Form(
      '',
      title,
      description,
      resourceType,
      resourceId,
      formType,
      status,
      anonymous
    )
    return f
  }

  /**
   * Add an element to the form
   *
   * @param element - The element to add
   * @returns The form instance
   */
  addElement(element: Element): Form {
    element.sectionId = undefined
    element.position = this.elements.length
    this.elements.push(element)
    this.sortElements()
    return this
  }

  /**
   * Remove an element from the form
   *
   * @param elementId - The ID of the element to remove
   */
  removeElement(elementId: string): void {
    this.elements = this.elements.filter((element) => element.id !== elementId)
    this.updatePositions()
  }

  /**
   * Get an element by its ID
   *
   * @param elementId - The ID of the element to get
   * @returns The element with the specified ID, or undefined if not found
   */
  getElement(elementId: string): Element | null {
    for (const element of this.elements) {
      if (element.id === elementId) {
        return element
      }

      if (element instanceof SectionElement) {
        for (const child of element.elements) {
          if (child.id === elementId) {
            return child
          }
        }
      }
    }
    return null
  }

  /**
   * Sort elements by their position
   */
  sortElements(): void {
    this.elements.sort((a, b) => a.position - b.position)
  }

  /**
   * Update positions of all elements to ensure they are sequential
   */
  updatePositions(): void {
    this.elements.forEach((element, index) => {
      element.position = index
    })
  }

  /**
   * Validate all elements in the form
   *
   * @returns True if all elements are valid, false otherwise
   */
  validate(): boolean {
    // Check if there are any elements
    if (this.elements.length === 0) {
      return false
    }

    // Check if the title is not empty
    if (!this.title.trim()) {
      return false
    }

    // Validate each element
    for (const element of this.elements) {
      if (!element.validate()) {
        return false
      }
    }

    return true
  }

  /**
   * Get a custom parameter value
   *
   * @param key - The key of the custom parameter
   * @param defaultValue - The default value to return if the parameter doesn't exist
   */
  getParam<T>(key: string, defaultValue?: T): T | undefined {
    const value = this.params[key]

    if (key in this.params && isValidParam<T>(value, defaultValue)) {
      return value
    }

    return defaultValue
  }

  /**
   * Set a custom parameter value
   *
   * @param key - The key of the custom parameter
   * @param value - The value to set
   */
  setParam<T>(key: string, value: T): void {
    this.params[key] = value
  }

  /**
   * Delete a custom parameter value
   *
   * @param key - The key of the custom parameter
   */
  deleteParam(key: string): void {
    delete this.params[key]
  }

  /**
   * Get all elements of a specific type
   *
   * @param type - The type of elements to get
   * @returns An array of elements of the specified type
   */
  getElementsByType(type: string): Element[] {
    return this.elements.filter((element) => element.getType() === type)
  }

  /**
   * Get the number of elements in the form
   */
  getElementCount(sectionId?: string): number {
    if (sectionId) {
      return this.elements.filter((el) => el.sectionId === sectionId).length
    } else {
      return this.elements.length
    }
  }

  /**
   * Get the number of mandatory elements in the form
   */
  getMandatoryElementCount(): number {
    return this.elements.filter((element) => element.mandatory).length
  }
  /**
   * Get the answers in the backend format
   */
  getAnswers(): AnswersBackend[] {
    const answers: AnswersBackend[] = []
    this.elements.map((el: Element | SectionElement) => {
      if (isSectionElement(el)) {
        return el.elements.map((e) => {
          if (e.answer)
            answers.push({
              answer: e.answer.answer,
              comment: e.answer.comment,
              options: e.answer.options,
              elementId: e.id,
            })
        })
      } else {
        if (el.answer)
          answers.push({
            answer: el.answer.answer,
            comment: el.answer.comment,
            options: el.answer.options,
            elementId: el.id,
          })
      }
    })
    return answers
  }

  hasUnansweredMandatoryElements(): boolean {
    const unanswered = this.elements.find((el) => {
      if (isSectionElement(el)) {
        return el.elements.find(
          (e) => e.mandatory && (!e.answer || !e.answer.answer || e.answer.options?.length === 0)
        )
      } else {
        return el.mandatory && (!el.answer || !el.answer.answer || el.answer.options?.length === 0)
      }
    })
    return !!unanswered
  }

  resetFormAnswers(): void {
    this.elements.forEach((el) => {
      if (isSectionElement(el)) {
        el.elements.map((e) => (e.answer = null))
      } else {
        el.answer = null
      }
    })
  }

  /**
   * Returns the total number of answers within the elements.
   *
   * @return {number} The total count of answers found in the elements.
   */
  numberOfAnswers(): number {
    let answers = 0
    this.elements.forEach((el) => {
      if (isSectionElement(el)) {
        answers += el.elements.filter((e) => e.hasAnswer()).length
      } else if (el.hasAnswer()) {
        answers += 1
      }
    })
    return answers
  }
}
